import React, { useState, useCallback } from 'react';
import { Button, Text } from '@bambuser-ui';

const Tooltip = ({
  title,
  content,
  children,
  backgroundColor = 'black',
  textColor = 'white',
  position = 'right',
  positionStyles,
  offsetMargin = 4,
  borderRadius = '0px',
  show = true,
  style: overrideStyles = {
    width: 296,
  },
  textStyle = {
    padding: '4px 8px',
  },
  wrapperClassName,
  wrapperStyle = {},
  onPointerEnter,
  onPointerLeave,
  forceShow: forceShowInitial = false,
  forceShowOnClick = () => {},
  buttonText,
  buttonStyle,
  ...attr
}) => {
  const [hover, setHover] = useState(false);
  const [forceShow, setForceShow] = useState(forceShowInitial);
  const [tooltipHeight, setTooltipHeight] = useState(0);
  const targetRef = useCallback(node => {
    if (node && !tooltipHeight) {
      setTooltipHeight(node.offsetHeight);
    }
  },[]);

  const handlePointerEnter = () => {
    setHover(true);
    onPointerEnter?.();
  };

  const handlePointerLeave = () => {
    setHover(false);
    onPointerLeave?.();
  };

  let positionStyle = {};
  let arrowPositionStyle = {};
  const { width } = overrideStyles;

  switch (position) {
    case 'bottom':
      positionStyle = {
        top: (tooltipHeight / 2) + offsetMargin,
        transform: 'translate(-50%)',
      };
      arrowPositionStyle = {
        top: -10,
        transform: `translate(${(width/2) - 5}px)`, // The arrow translation is half the width of the tooltip minus half of the width of the arrow itself
      };
      break;
    case 'top':
      positionStyle = {
        top: -(tooltipHeight / 2) - offsetMargin,
        transform: 'translate(-50%, -50%)',
      };
      arrowPositionStyle = {
        top: 0,
        transform: `rotate(180deg) translate(-${(width/2) - 5}px, -${tooltipHeight}px)`, // The arrow translation is half the width of the tooltip minus half of the width of the arrow itself
      };
      break;
    case 'left':
      positionStyle = {
        top: (tooltipHeight / 2) + offsetMargin,
        transform: 'translate(-103%, -100%)',
      };
      arrowPositionStyle = {
        transform: `rotate(90deg) translate(${tooltipHeight/2}px, -${width}px)`,
      };
      break;
    case 'right':
      positionStyle = {
        top: (tooltipHeight / 2) + offsetMargin,
        transform: 'translate(7%, -100%)',
      };
      arrowPositionStyle = {
        transform: `rotate(-90deg) translate(-${tooltipHeight/2}px, -99%)`,
      };
      break;
  }

  // If `positionStyles` is specified then the position will be overridden
  if (typeof positionStyles === 'function') {
    const pos = positionStyles(tooltipHeight, offsetMargin, width);
    positionStyle = pos.positionStyle;
    arrowPositionStyle = pos.arrowPositionStyle;
  } else if (typeof positionStyles === 'object') {
    // Still wanna support with hardcoded object
    positionStyle = positionStyles.positionStyle ?? positionStyle;
    arrowPositionStyle = positionStyles.arrowPositionStyle ?? arrowPositionStyle;
  }

  const handleClose = () => {
    setHover(false);
    setForceShow(false);
    forceShowOnClick();
  };

  return (
    <div
      className={wrapperClassName}
      style={{ ...styles.container, ...wrapperStyle }}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
    >
      {children}
      {(forceShow || (hover && show)) && (
        <div
          style={{
            ...styles.tooltip,
            ...positionStyle,
            backgroundColor,
            borderRadius,
            width,
            ...(!!tooltipHeight && { visibility: 'visible' }),
            ...overrideStyles,
          }}
          ref={targetRef}
          {...attr}
        >
          <div style={{ ...styles.tooltipArrow, ...arrowPositionStyle, borderColor: `transparent transparent ${backgroundColor} transparent` }} />
          {title && (
            <Text type="meta" style={{ ...styles.textContainer, color: textColor, ...textStyle }}>
              {title}
            </Text>
          )}
          {content && (
            <div style={{ ...styles.contentContainer }}>
              {content}
            </div>
          )}
          {forceShow && (
            <Button type="text" style={{ ...styles.closeButton, ...buttonStyle }} onClick={handleClose}>
              {buttonText}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    position: 'relative',
  },
  tooltip: {
    transition: '0s visibility',
    position: 'absolute',
    visibility: 'hidden',
    zIndex: 999999,
    cursor: 'auto',
  },
  tooltipArrow: {
    content: '""',
    position: 'absolute',
    borderWidth: '5px',
    borderStyle: 'solid',
  },
  contentContainer: {
    padding: 16,
  },
  textContainer: {
    color: '#FFF',
    textAlign: 'left',
    margin: 0,
    fontSize: 14,
    fontWeight: 'normal',
  },
  closeButton: {
    color: '#fff',
    padding: 16,
    textTransform: 'none',
    fontSize: 14,
  },
};

export default Tooltip;
